import {
  LabelType,
  type ParticipantDocument,
  type UpdateParticipantDocumentRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedLabelChooser } from '../../components/forms/HookedLabelChooser';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateParticipantDocumentMutation } from '../../graphql/hooks/useUpdateParticipantDocumentMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';

type FormValues = UpdateParticipantDocumentRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().nullish(),
  labelIds: z.array(z.string()).nullish(),
});

interface Props {
  document: ParticipantDocument;
}

export const ParticipantDocumentEditModal = createModal<Props>((props) => {
  const { document, modal } = props;

  const { remove, hide, visible } = modal;
  const { organization, successToast, errorToast } = useAppContext();
  const mutation = useUpdateParticipantDocumentMutation();

  const initialValues: Partial<FormValues> = {
    name: document.name,
    labelIds: document.labels.map((label) => label.id),
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantDocumentId: document.id,
          participantId: document.participantId,
          request: values,
        })
        .then(async () => {
          hide();
          successToast('Document updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating document: ${err.message}`);
        });
    },
    [
      mutation,
      organization.id,
      document.id,
      document.participantId,
      hide,
      successToast,
      errorToast,
    ],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a document'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <FormStack>
                  <HookedFormField name='name' label='Name' h='full'>
                    <HookedInput name='name' />
                  </HookedFormField>

                  <HookedFormField name='labelIds' label='Labels'>
                    <HookedLabelChooser name='labelIds' type={LabelType.ParticipantDocument} />
                  </HookedFormField>
                </FormStack>
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button size='sm' onClick={hide} variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
